import axios from 'axios';
import { formatApiError } from './AuthService';

const SEARCH_BASE = 'https://login.flymiles.com.au/api_sections/agents_dashboard/search_booking_form';
const AGENTS_BASE = 'https://login.flymiles.com.au/api_sections/agents_dashboard';

function rejectIfApiError(response) {
    if (response.data && response.data.error) {
        return Promise.reject({ response: { data: response.data } });
    }
    return response;
}

export function searchLocations(query, otherLabel) {
    return axios
        .post(`${SEARCH_BASE}/search_bar.php`, { search_results: query, search_from_to: otherLabel || '' })
        .then((res) => (Array.isArray(res.data) ? res.data : []));
}

export function getPostalCode(placeId) {
    return axios
        .post(`${SEARCH_BASE}/get_postal_code.php`, { place_id: placeId })
        .then((res) => res.data && res.data.postal_code);
}

export function searchTransfers(payload) {
    return axios.post(`${SEARCH_BASE}/search_results.php`, payload).then(rejectIfApiError).then((res) => res.data);
}

export function getExtrasPricing() {
    return axios.get(`${SEARCH_BASE}/extras_prices.php`).then((res) => res.data);
}

export function getSingleVehicleDetail(vehicleId) {
    // API returns a single-element array, same pattern as getAgentDetail.
    return axios
        .post(`${SEARCH_BASE}/get_single_vehicle_detail.php`, { vehicle_id: vehicleId })
        .then((res) => (Array.isArray(res.data) ? res.data[0] : res.data) || null);
}

export function createBooking(payload) {
    return axios.post(`${SEARCH_BASE}/create_booking.php`, payload).then(rejectIfApiError).then((res) => res.data);
}

// Sends the booking confirmation email.
export function sendBookingEmail(bookingId) {
    return axios
        .post(`${AGENTS_BASE}/send_booking_email.php`, { booking_id: bookingId, preview: false })
        .then(rejectIfApiError)
        .then((res) => res.data);
}

export function createQuoteRequest(payload) {
    return axios.post(`${SEARCH_BASE}/create_get_quote.php`, payload).then(rejectIfApiError).then((res) => res.data);
}

export function stripePayment(payload) {
    return axios.post(`${SEARCH_BASE}/stripe_payment_api.php`, payload).then(rejectIfApiError).then((res) => res.data);
}

export function getAgentDetail(agentId) {
    // API returns a single-element array ([{currency, credit, credit_limit}]), not a bare object.
    return axios
        .post(`${SEARCH_BASE}/get_agent_detail.php`, { agent_id: agentId })
        .then((res) => (Array.isArray(res.data) ? res.data[0] : res.data) || {});
}

export function payViaCredit(payload) {
    return axios.post(`${SEARCH_BASE}/payment_credit_limit.php`, payload).then(rejectIfApiError).then((res) => res.data);
}

// Shared, non-agent-scoped endpoint (just exposes the Stripe publishable key) — not in travel_api.json,
// carried over from the existing public-site implementation per the integration doc.
export function fetchStripePublicKey() {
    return axios
        .post('https://login.flymiles.com.au/api_sections/tours/stripe_pbublic_key_agent.php', { stripe_key: 'yes' })
        .then((res) => res.data && res.data.stripe_key);
}

export function getUpcomingBookings(agentId) {
    return axios
        .post(`${AGENTS_BASE}/upcoming_bookings.php`, { agent_id: agentId })
        .then((res) => (Array.isArray(res.data) ? res.data : []));
}

// Send `days` by default (no from_date/to_date). Once the caller passes an explicit
// fromDate/toDate (user picked a date range), send those instead of days — never both.
export function getBookingDetails({ agentId, type, days, fromDate, toDate }) {
    const body = { agent_id: agentId, type };
    if (fromDate && toDate) {
        body.from_date = fromDate;
        body.to_date = toDate;
    } else {
        body.days = String(days);
    }
    return axios
        .post(`${AGENTS_BASE}/get_booking_detail.php`, body)
        .then((res) => (Array.isArray(res.data) ? res.data : []));
}

export function getStatusDropdown() {
    return axios.get(`${AGENTS_BASE}/get_status_dropdown.php`).then((res) => res.data || {});
}

export function getSingleBookingDetail(bookingId) {
    // API returns a single-element array, same pattern as getAgentDetail.
    return axios
        .post(`${AGENTS_BASE}/get_single_booking_detail.php`, { booking_id: bookingId })
        .then((res) => (Array.isArray(res.data) ? res.data[0] : res.data) || null);
}

export function editBooking(payload) {
    return axios.post(`${AGENTS_BASE}/edit_booking.php`, payload).then(rejectIfApiError).then((res) => res.data);
}

export function cancelBooking(payload) {
    return axios.post(`${AGENTS_BASE}/cancelled_booking.php`, payload).then(rejectIfApiError).then((res) => res.data);
}

// Status id the API uses for a cancelled booking.
export const CANCELLED_STATUS_ID = '4';

export { formatApiError };

// The API only returns a bare numeric booking_id (e.g. 23) — the corrections doc asked
// for a short, recognizable reference instead of the long internal ref number, so we
// derive one client-side rather than showing the raw id.
export function formatBookingReference(bookingId) {
    if (bookingId === null || bookingId === undefined || bookingId === '') return '';
    return `AU2${String(bookingId).padStart(6, '0')}`;
}

// API returns `price` (default rate), `transfer_rate` (agent-specific rate, overrides price
// when set) and `discounted_rate` (agent-specific discount off transfer_rate/price, when set) -
// discounted_rate is what agents are actually charged, so it must win when present.
export function computeEffectivePrice({ price, transfer_rate, discounted_rate }) {
    const hasRate = transfer_rate !== null && transfer_rate !== undefined && transfer_rate !== '';
    const base = Number(hasRate ? transfer_rate : price) || 0;
    const hasDiscount = discounted_rate !== null && discounted_rate !== undefined && discounted_rate !== '';

    if (hasDiscount) {
        const discounted = Number(discounted_rate);
        return { effective: discounted, original: base, discounted: discounted !== base };
    }
    return { effective: base, original: base, discounted: false };
}
